import { motion } from "framer-motion";
import { Gauge } from "lucide-react";
import { Field, Label } from "./../components/UI/fieldset.jsx";
import { Input } from "../components/UI/input";
import { Listbox, ListboxLabel, ListboxOption } from "../components/UI/listbox";
import { Radio, RadioGroup } from "../components/UI/radio";
import { Textarea } from "../components/UI/textarea";
import { Button } from "../components/UI/button";

export default function Screening() {
  const timeframes = ["Week", "Month", "Quarter", "Year"];
  const questions = [
    "Do you use a multi-application process?",
    "Do you conduct background checks?",
    "Is your screening process consistent for all volunteers?",
  ];
  const previousAssessments = [
    "1 June 2015",
    "31 December 2020",
    "1 January 2024",
  ];

  return (
    <div className="flex h-full">
      {/* Left Column - Main Content */}
      <main className="flex-1 p-6 pt-0">
        {/* Self Rating Section */}
        <section className="mt-6">
          <h2 className="text-xl font-semibold text-marigold">Self Rating</h2>
          <Field className="mt-2 flex items-center gap-4">
            <Label>
              How pleased are you with your application and screening processes?
              (1-10)
            </Label>
            <Input type="number" className="w-16" min="1" max="10" />
          </Field>
        </section>

        {/* Key Questions Section */}
        <section className="mt-6">
          <h2 className="text-xl font-semibold text-marigold">Key Questions</h2>
          {/* Column Headers */}
          <div className="grid grid-cols-[1.5fr_0.3fr_0.3fr_3fr] gap-4 mt-2 font-semibold">
            <span></span> {/* Placeholder for Question Column */}
            <span className="text-center">Yes</span>
            <span className="text-center">No</span>
            <span></span> {/* Placeholder for Text Input Column */}
          </div>

          <div className="mt-2">
            {questions.map((question, index) => (
              <RadioGroup
                key={index}
                className="grid grid-cols-[1.5fr_0.3fr_0.3fr_3fr] items-center gap-4"
              >
                <Label>{question}</Label>
                <div className="flex justify-center">
                  <Radio value="yes" />
                </div>
                <div className="flex justify-center">
                  <Radio value="no" />
                </div>
                <Input
                  placeholder={`Reason for selecting "no"`}
                  className="w-full"
                />
              </RadioGroup>
            ))}
          </div>
        </section>

        {/* Reflection Section */}
        <section className="mt-6">
          <h2 className="text-xl font-semibold text-marigold">Reflection</h2>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <Field>
              <Label>What makes your screening process run smoothly?</Label>
              <Textarea className="mt-2" />
            </Field>
            <Field>
              <Label>
                Where do you face difficulties in screening volunteers?
              </Label>
              <Textarea className="mt-2" />
            </Field>
          </div>
        </section>
      </main>

      {/* Right Column - Metrics & Previous Assessments */}
      <aside className="w-1/4 p-6 flex flex-col">
        <h2 className="text-xl font-semibold text-marigold">Metrics</h2>

        {/* Block 1 - Average Days to Process */}
        <div className="bg-white shadow-md rounded-lg p-3 mt-4 flex flex-col items-center">
          <h3 className="text-md font-semibold text-center">
            Average Days to Process
          </h3>
          <div className="w-16 h-16 flex items-center justify-center rounded-full border-4 border-marigold mt-3">
            <span className="text-2xl font-bold text-marigold">30</span>
          </div>
          <Field className={"mt-2"}>
            <Listbox defaultValue="Week">
              {timeframes.map((tf) => (
                <ListboxOption key={tf} value={tf}>
                  <ListboxLabel>{tf}</ListboxLabel>
                </ListboxOption>
              ))}
            </Listbox>
          </Field>
        </div>

        {/* Block 2 - Conversion Rate Applicants to Accepted */}
        <div className="bg-white shadow-md rounded-lg p-3 mt-4 flex flex-col items-center">
          <h3 className="text-md font-semibold text-center">Conversion Rate</h3>
          <h3 className="text-md font-semibold text-center">
            Applicants to Accepted
          </h3>
          <motion.div
            className="mt-3"
            initial={{ rotate: -30 }}
            animate={{ rotate: 30 }}
            transition={{
              repeat: Infinity,
              duration: 2,
              repeatType: "reverse",
            }}
          >
            <Gauge className="w-12 h-12" />
          </motion.div>
        </div>

        {/* Previous Assessments Box */}
        <div className="bg-sky shadow-md rounded-lg p-3 mt-auto border-2 border-marigold flex flex-col items-start">
          <h2 className="text-md font-semibold">Previous Assessments</h2>
          <ul className="mt-2 space-y-1 text-sm">
            {previousAssessments.map((date, index) => (
              <li
                key={index}
                className="text-blue-600 underline cursor-pointer"
              >
                {date}
              </li>
            ))}
          </ul>
          <Button outline className="mt-2 self-end">
            See all
          </Button>
        </div>
      </aside>
    </div>
  );
}
